import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  PolicyRuleErrors,
  PolicyRuleInput,
  validatePolicyRule,
} from "@/components/pages/general-info/utils/policyRule";

interface PolicyRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (rule: PolicyRuleInput) => void;
}

export const PolicyRuleDialog: React.FC<PolicyRuleDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [withIcon, setWithIcon] = useState(false);
  const [errors, setErrors] = useState<PolicyRuleErrors>({});

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setTitle("");
      setDescription("");
      setWithIcon(false);
      setErrors({});
    }
    onOpenChange(next);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const nextErrors = validatePolicyRule({ title, description });
    setErrors(nextErrors);
    if (nextErrors.title || nextErrors.description) return;
    onSubmit({ title, description, withIcon });
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {t("hotels.generalInfo.policyRule.title")}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Input
              label={t("hotels.generalInfo.policyRule.ruleTitle")}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              aria-invalid={Boolean(errors.title)}
              autoFocus
            />
            {errors.title && (
              <p role="alert" className="text-destructive mt-1 text-sm">
                {t("hotels.generalInfo.policyRule.titleRequired")}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="policy-rule-icons" className="text-sm font-medium">
              {t("hotels.generalInfo.policyRule.icons")}
            </label>
            <Switch
              id="policy-rule-icons"
              checked={withIcon}
              onCheckedChange={setWithIcon}
            />
          </div>
          <div>
            <Textarea
              label={t("hotels.generalInfo.policyRule.description")}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              aria-invalid={Boolean(errors.description)}
            />
            {errors.description && (
              <p role="alert" className="text-destructive mt-1 text-sm">
                {t("hotels.generalInfo.policyRule.descriptionRequired")}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="new_secondary"
              onClick={() => handleOpenChange(false)}
            >
              {t("hotels.generalInfo.policyRule.cancel")}
            </Button>
            <Button type="submit" variant="new_default">
              {t("hotels.generalInfo.addRule")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
